import {useEffect, useMemo, useState} from 'react'
import {captureMessage, withScope} from '@sentry/react'

import {twMerge} from '@sdox/utils'

import {getPlaceholderDataUrl} from './utils'

import type {ComponentProps, ReactEventHandler} from 'react'

export interface LazyImageProps
  extends Omit<ComponentProps<'img'>, 'ref' | 'alt' | 'src'> {
  src?: string | null
  alt?: string
  placeholderSrc?: string | null
  loading?: 'lazy' | 'eager'
  onPlaceholderLoad?: ComponentProps<'img'>['onLoad']
  onPlaceholderError?: ComponentProps<'img'>['onError']
  logPlaceholderError?: boolean
  logError?: boolean
  hasBackground?: boolean
}

export enum ImageType {
  Placeholder = 'placeholder',
  Highres = 'highres',
}

const captureLoadErrorException = (
  image: HTMLImageElement,
  imageType: ImageType,
  src: ComponentProps<'img'>['src'] | null,
) => {
  const imageContext = {
    imageType,
    alt: image.alt,
    imageElementSrc: image.src,
    imageComponentSrc: src,
  }

  withScope(scope => {
    scope.setTag('image.alt', image.alt)
    scope.setTag('image.type', imageType)
    captureMessage(
      'Could not load image.',
      {
        level: 'error',
        contexts: {image: imageContext},
      },
    )
  })
}

export const LazyImage = (props: LazyImageProps) => {
  const {
    src = '',
    placeholderSrc = src,
    className = '',
    alt = '',
    hasBackground = true,
    loading = 'lazy',
    onLoad: onSrcLoad,
    onError: onSrcError,
    onPlaceholderLoad: onPlaceholderLoadProp,
    onPlaceholderError: onPlaceholderErrorProp,
    logError = true,
    logPlaceholderError = false, // we don't use it everywhere because it will often throw an error
    ...restProps
  } = props

  const [hasPlaceholderLoaded, setHasPlaceholderLoaded] = useState(false)
  const [hasPlaceholderError, setHasPlaceholderError] = useState(false)
  const [hasSourceLoaded, setHasSourceLoaded] = useState(false)
  const [hasSrcError, setHasSrcError] = useState<boolean>(false)

  // when the placeholderSrc is an empty string, the img will not throw onerror or onload,
  //  so we need to set the error manually
  useEffect(() => {
    if (!placeholderSrc) setHasPlaceholderError(true)
  }, [placeholderSrc])

  // reset everything when src changes
  useEffect(() => {
    setHasSourceLoaded(false)
    setHasSrcError(false)
    setHasPlaceholderLoaded(false)
    setHasPlaceholderError(false)
  }, [src])

  const hasPlaceholderFinished = hasPlaceholderLoaded || hasPlaceholderError
  const hasSrcFinished = hasSourceLoaded || hasSrcError
  const showSkeletonPlaceholder = hasPlaceholderError && hasSrcError

  // the placeholder dataUrl only needs to be calculated when it is actually shown
  const skeletonPlaceholderSrc = useMemo(() => {
    return showSkeletonPlaceholder
      ? getPlaceholderDataUrl(restProps.width, restProps.height)
      : ''
  }, [showSkeletonPlaceholder, restProps.width, restProps.height])

  // show fallback if both placeholder and src could not be loaded
  if (showSkeletonPlaceholder) {
    return (
      <img
        data-testid={'img-skeleton-placeholder'}
        src={skeletonPlaceholderSrc}
        alt={alt}
        className={twMerge('bg-media-bg', className)}
        {...restProps}
      />
    )
  }

  const onPlaceholderLoad: ReactEventHandler<HTMLImageElement> = (event) => {
    setHasPlaceholderLoaded(true)
    onPlaceholderLoadProp?.(event)
  }

  const onPlaceholderError: ReactEventHandler<HTMLImageElement> = (event) => {
    if (logPlaceholderError) captureLoadErrorException(event.currentTarget, ImageType.Placeholder, placeholderSrc)
    setHasPlaceholderError(true)
    onPlaceholderErrorProp?.(event)
  }

  const onLoad: ReactEventHandler<HTMLImageElement> = (event) => {
    setHasSourceLoaded(true)
    onSrcLoad?.(event)
  }

  const onError: ReactEventHandler<HTMLImageElement> = (event) => {
    if (logError) captureLoadErrorException(event.currentTarget, ImageType.Highres, src)
    setHasSrcError(true)
    onSrcError?.(event)
  }

  // show placeholder
  //    while source is loading
  //    if the source had and error but the placeholder was loaded
  const showPlaceholder = (
    !hasSrcFinished ||
      (hasSrcError && hasPlaceholderLoaded)
  )

  return (
    <>
      {showPlaceholder && (
        <img
          data-testid={'img-placeholder'}
          src={!placeholderSrc ? getPlaceholderDataUrl() : placeholderSrc}
          alt={alt}
          className={twMerge(
            !hasPlaceholderFinished && 'animate-pulse bg-surface-tertiary',
            hasBackground && 'bg-media-bg',
            className,
          )}
          onLoad={onPlaceholderLoad}
          onError={onPlaceholderError}
          loading={loading}
          {...restProps}
        />
      )}
      {hasPlaceholderFinished && (
        <img
          data-testid={'img-src'}
          src={src ?? ''}
          alt={alt}
          loading={loading}
          // size-0 might prevent browsers from loading images, but for now it looks good.
          className={twMerge(
            hasSourceLoaded ? className : 'absolute size-0',
            hasBackground && 'bg-media-bg',
          )}
          onLoad={onLoad}
          onError={onError}
          {...restProps}
        />
      )}
    </>
  )
}
